import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { inventoryAPI, boutiquesAPI, productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Package, 
  AlertTriangle, 
  Plus, 
  Minus,
  Edit,
  Layers,
  Store,
  ShoppingBag,
  Building2,
  Globe,
  Trash2,
  RefreshCw,
  TrendingDown,
  CheckCircle
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import { HelpTooltip, PageIntroBanner, EmptyStateGuide } from '../components/ui/help-tooltip';
import { HelpButton } from '../components/HelpButton';

const CHANNEL_ICONS = {
  event: Store,
  etsy: ShoppingBag,
  boutique: Building2,
  website: Globe,
  farmers_market: Store,
  wholesale: Package,
};

const CHANNEL_LABELS = {
  event: 'Events',
  etsy: 'Etsy',
  boutique: 'Boutique',
  website: 'Website',
  farmers_market: 'Farmers Market',
  wholesale: 'Wholesale',
  reserved: 'Reserved',
};

export const Inventory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAllocateDialogOpen, setIsAllocateDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Forms
  const [stockForm, setStockForm] = useState({ total_stock: 0, low_stock_threshold: 5 });
  const [allocateForm, setAllocateForm] = useState({ 
    channel_type: 'event', 
    channel_id: '', 
    allocated_quantity: 1 
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [inventoryRes, alertsRes, boutiquesRes] = await Promise.all([
        inventoryAPI.getAll(),
        inventoryAPI.getLowStockAlerts(),
        boutiquesAPI.getAll({ active_only: true }),
      ]);
      setInventory(inventoryRes.data);
      setLowStockAlerts(alertsRes.data);
      setBoutiques(boutiquesRes.data);
    } catch (error) {
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStock = async () => {
    if (!selectedProduct) return;
    try {
      await inventoryAPI.update(selectedProduct.product_id || selectedProduct.id, {
        total_stock: parseInt(stockForm.total_stock),
        low_stock_threshold: parseInt(stockForm.low_stock_threshold),
      });
      toast.success('Inventory updated');
      setIsEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update inventory');
    }
  };

  const handleAllocate = async () => {
    if (!selectedProduct) return;
    const productId = selectedProduct.product_id || selectedProduct.id;
    try {
      await inventoryAPI.allocate(productId, {
        product_id: productId,
        channel_type: allocateForm.channel_type,
        channel_id: allocateForm.channel_type === 'boutique' ? allocateForm.channel_id : null,
        allocated_quantity: parseInt(allocateForm.allocated_quantity),
      });
      toast.success('Inventory allocated');
      setIsAllocateDialogOpen(false);
      setAllocateForm({ channel_type: 'event', channel_id: '', allocated_quantity: 1 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate inventory');
    }
  };

  const handleRemoveAllocation = async (productId, allocationId) => {
    try {
      await inventoryAPI.removeAllocation(productId, allocationId);
      toast.success('Allocation removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove allocation');
    }
  };

  const openEditDialog = (item) => {
    setSelectedProduct(item);
    setStockForm({
      total_stock: item.total_quantity || item.total_stock || 0,
      low_stock_threshold: item.reorder_point || item.low_stock_threshold || 5,
    });
    setIsEditDialogOpen(true);
  };

  const openAllocateDialog = (item) => {
    setSelectedProduct(item);
    setAllocateForm({ channel_type: 'event', channel_id: '', allocated_quantity: 1 });
    setIsAllocateDialogOpen(true);
  };

  // Stats
  const totalProducts = inventory.length;
  const totalStock = inventory.reduce((sum, i) => sum + (i.total_quantity || i.total_stock || 0), 0);
  const totalAllocated = inventory.reduce((sum, i) => sum + (i.allocated_quantity || i.allocated || 0), 0);
  const lowStockCount = lowStockAlerts.length;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro Banner */}
        <PageIntroBanner
          title="Inventory Management"
          description="Track your product stock levels, allocate inventory to different sales channels, and get alerts when items are running low."
          icon={Package}
          features={['Stock Tracking', 'Channel Allocation', 'Low Stock Alerts', 'Auto-deduct on Sale']}
          dismissKey="inventory_intro"
        />

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit'] flex items-center gap-2">
                Inventory
                <HelpTooltip content="Track stock levels for each product and allocate inventory to different sales channels to prevent overselling." />
              </h1>
              <p className="text-gray-600 mt-1">Manage stock levels and channel allocations</p>
            </div>
            <HelpButton tutorialId="inventory" />
          </div>
          <Button onClick={fetchData} variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Products</p>
                <p className="text-2xl font-bold text-[#1E3A5F]">{totalProducts}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Layers className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Stock</p>
                <p className="text-2xl font-bold text-green-600">{totalStock}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Store className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Allocated</p>
                <p className="text-2xl font-bold text-purple-600">{totalAllocated}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Low Stock</p>
                <p className="text-2xl font-bold text-amber-600">{lowStockCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Low Stock Alerts */}
        {lowStockAlerts.length > 0 && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <h2 className="font-semibold text-amber-800">Low Stock Alerts</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {lowStockAlerts.map((alert) => (
                <div
                  key={alert.product_id || alert.id}
                  className="px-3 py-2 rounded-lg bg-amber-100 text-amber-700"
                >
                  <span className="font-medium">{alert.name}</span>
                  <span className="ml-2 text-sm">
                    ({alert.total_quantity || 0} left)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inventory List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
          </div>
        ) : inventory.length === 0 ? (
          <EmptyStateGuide
            icon={Package}
            title="No products yet"
            description="Add products to start tracking inventory"
            actionLabel="Add Products"
            onAction={() => navigate('/products')}
            tips={[
              "Products can have stock levels set individually",
              "Allocate inventory to different channels to prevent overselling",
              "Set low stock thresholds to get alerts"
            ]}
            testId="empty-inventory"
          />
        ) : (
          <div className="space-y-4">
            {inventory.map((item) => {
              const isLowStock = item.low_stock;
              const isNegative = item.available_quantity < 0;
              const stockPercent = item.total_quantity > 0 
                ? Math.max(0, Math.min(100, Math.round((item.available_quantity / item.total_quantity) * 100)))
                : 0;

              return (
                <div
                  key={item.product_id || item.id}
                  className={`bg-white rounded-xl border-2 p-4 shadow-hard ${
                    isNegative ? 'border-red-400' : isLowStock ? 'border-amber-400' : 'border-[#1E3A5F]'
                  }`}
                  data-testid={`inventory-${item.product_id || item.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${isNegative ? 'bg-red-100' : isLowStock ? 'bg-amber-100' : 'bg-blue-100'}`}>
                        <Package className={`w-6 h-6 ${isNegative ? 'text-red-600' : isLowStock ? 'text-amber-600' : 'text-blue-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1E3A5F]">{item.name}</h3>
                        <p className="text-sm text-gray-500">{item.category || 'No category'}</p>
                        
                        {/* Stock bar */}
                        <div className="mt-2 w-48">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span className={isNegative ? 'text-red-600 font-semibold' : ''}>
                              Available: {item.available_quantity}
                            </span>
                            <span>Total: {item.total_quantity}</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                isNegative ? 'bg-red-500' :
                                stockPercent <= 25 ? 'bg-red-500' :
                                stockPercent <= 50 ? 'bg-amber-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${isNegative ? '100%' : stockPercent + '%'}` }}
                            />
                          </div>
                        </div>
                        
                        {isNegative && (
                          <div className="flex items-center gap-1 mt-2 text-red-600 text-sm font-medium">
                            <TrendingDown className="w-4 h-4" />
                            Negative inventory - oversold by {Math.abs(item.available_quantity)}
                          </div>
                        )}
                        
                        {!isNegative && isLowStock && (
                          <div className="flex items-center gap-1 mt-2 text-amber-600 text-sm">
                            <AlertTriangle className="w-4 h-4" />
                            Low stock (threshold: {item.reorder_point})
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(item)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit Stock
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openAllocateDialog(item)}
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Allocate
                      </Button>
                    </div>
                  </div>

                  {/* Allocations */}
                  {item.allocations && item.allocations.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium text-gray-600 mb-2">Channel Allocations</p>
                      <div className="flex flex-wrap gap-2">
                        {item.allocations.map((alloc) => {
                          const Icon = CHANNEL_ICONS[alloc.channel] || Package;
                          return (
                            <div
                              key={alloc.id}
                              className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg"
                            >
                              <Icon className="w-4 h-4 text-gray-600" />
                              <span className="text-sm">
                                {alloc.channel_name || alloc.channel}: {alloc.quantity}
                              </span>
                              <button
                                onClick={() => handleRemoveAllocation(item.product_id || item.id, alloc.id)}
                                className="text-red-500 hover:text-red-700 ml-1"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Stock Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Stock - {selectedProduct?.product.name}</DialogTitle>
            <DialogDescription>Update stock levels and low stock threshold</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-1">
                Total Stock
                <HelpTooltip content="The total number of this product you have on hand." />
              </Label>
              <Input
                type="number"
                min="0"
                value={stockForm.total_stock}
                onChange={(e) => setStockForm({...stockForm, total_stock: e.target.value})}
              />
            </div>
            <div>
              <Label className="flex items-center gap-1">
                Low Stock Threshold
                <HelpTooltip content="You'll get an alert when available stock drops to or below this number." />
              </Label>
              <Input
                type="number"
                min="0"
                value={stockForm.low_stock_threshold}
                onChange={(e) => setStockForm({...stockForm, low_stock_threshold: e.target.value})}
              />
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Currently allocated: <strong>{selectedProduct?.allocated || 0}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Available after update: <strong>{Math.max(0, (parseInt(stockForm.total_stock) || 0) - (selectedProduct?.allocated || 0))}</strong>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateStock} className="btn-primary">Update Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Dialog */}
      <Dialog open={isAllocateDialogOpen} onOpenChange={setIsAllocateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Allocate Inventory - {selectedProduct?.product.name}</DialogTitle>
            <DialogDescription>
              Reserve inventory for a specific sales channel. Available: {selectedProduct?.available || 0}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Channel</Label>
              <Select
                value={allocateForm.channel_type}
                onValueChange={(v) => setAllocateForm({...allocateForm, channel_type: v, channel_id: ''})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">Events</SelectItem>
                  <SelectItem value="etsy">Etsy</SelectItem>
                  <SelectItem value="boutique">Boutique</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="wholesale">Wholesale</SelectItem>
                  <SelectItem value="reserved">Reserved (Hold)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {allocateForm.channel_type === 'boutique' && (
              <div>
                <Label>Boutique</Label>
                <Select
                  value={allocateForm.channel_id}
                  onValueChange={(v) => setAllocateForm({...allocateForm, channel_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select boutique" />
                  </SelectTrigger>
                  <SelectContent>
                    {boutiques.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Quantity to Allocate</Label>
              <Input
                type="number"
                min="1"
                max={selectedProduct?.available || 0}
                value={allocateForm.allocated_quantity}
                onChange={(e) => setAllocateForm({...allocateForm, allocated_quantity: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAllocateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAllocate} className="btn-primary">Allocate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;
